// Source unique de vérité pour tout le SEO du site.
// Consommée à la fois par <Head /> (côté client) et par scripts/prerender.js
// (au build, pour écrire les métas en dur dans le HTML servi aux crawlers).

const SITE_URL = "https://snkode.com";

// Identité — sert au JSON-LD schema.org
const person = {
  name: "Stéphane Nkengue",
  jobTitle: {
    fr: "Développeur web full stack",
    en: "Full stack web developer",
  },
  email: "nkenguestephane0@gmail.com",
  image: `${SITE_URL}/og/profil.jpg`,
  // Profils qui confirment l'identité auprès de Google (sameAs)
  sameAs: [
    "https://www.linkedin.com/in/stephane-nkengue-4bb27b242/",
    "https://github.com/StephaneNkengue",
    "https://thebridge-news.beehiiv.com/",
  ],
  address: {
    locality: "Montréal",
    region: "QC",
    country: "CA",
  },
  // Basé à Montréal, mais disponible partout dans la province
  areaServed: ["Montréal", "Québec", "Canada"],
  knowsAbout: [
    "React",
    "Next.js",
    "Vue.js",
    "Nuxt.js",
    "Node.js",
    "TypeScript",
    "ASP.NET Core",
    "SQL Server",
    "WordPress",
    "Tailwind CSS",
  ],
};

// Une entrée par (page × langue). Chaque entrée devient un fichier HTML
// statique au build, avec ses propres métas.
const pages = {
  fr: {
    lang: "fr-CA",
    ogLocale: "fr_CA",
    path: "/",
    title: "Stéphane Nkengue — Développeur web full stack à Montréal",
    description:
      "Développeur web full stack basé à Montréal, disponible partout au Québec. Je conçois des sites et applications web performants en React, Next.js, Vue et .NET.",
    ogImage: `${SITE_URL}/og/partage-fr.png`,
    ogImageAlt:
      "Stéphane Nkengue, développeur web full stack à Montréal — React, Next.js, Vue et .NET",
  },
  en: {
    lang: "en",
    ogLocale: "en_CA",
    path: "/en",
    title: "Stéphane Nkengue — Full stack web developer in Montreal",
    description:
      "Full stack web developer based in Montreal, available across Quebec. I build fast, reliable websites and web apps with React, Next.js, Vue and .NET.",
    ogImage: `${SITE_URL}/og/partage-en.png`,
    ogImageAlt:
      "Stéphane Nkengue, full stack web developer in Montreal — React, Next.js, Vue and .NET",
  },
};

// Construit le bloc JSON-LD d'une page. Deux entités liées :
//  - Person   : qui je suis (fait remonter un panneau d'entité sur mon nom)
//  - ProfilePage : ce qu'est cette page, rattachée à la Person
const buildJsonLd = (locale) => {
  const page = pages[locale];
  const pageUrl = `${SITE_URL}${page.path === "/" ? "/" : page.path}`;

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Person",
        "@id": `${SITE_URL}/#stephane-nkengue`,
        name: person.name,
        url: SITE_URL,
        image: person.image,
        email: `mailto:${person.email}`,
        jobTitle: person.jobTitle[locale],
        description: page.description,
        sameAs: person.sameAs,
        knowsAbout: person.knowsAbout,
        knowsLanguage: ["fr-CA", "en"],
        address: {
          "@type": "PostalAddress",
          addressLocality: person.address.locality,
          addressRegion: person.address.region,
          addressCountry: person.address.country,
        },
        homeLocation: {
          "@type": "City",
          name: person.address.locality,
        },
        worksFor: { "@id": `${SITE_URL}/#service` },
      },
      {
        // Activité freelance : c'est ce type que Google rattache aux
        // recherches de prestataires ("développeur web à Montréal").
        "@type": "ProfessionalService",
        "@id": `${SITE_URL}/#service`,
        name: person.name,
        url: SITE_URL,
        image: person.image,
        email: `mailto:${person.email}`,
        description: page.description,
        founder: { "@id": `${SITE_URL}/#stephane-nkengue` },
        address: {
          "@type": "PostalAddress",
          addressLocality: person.address.locality,
          addressRegion: person.address.region,
          addressCountry: person.address.country,
        },
        areaServed: person.areaServed.map((name) => ({
          "@type": "AdministrativeArea",
          name,
        })),
        availableLanguage: ["fr-CA", "en"],
        knowsAbout: person.knowsAbout,
      },
      {
        "@type": "ProfilePage",
        "@id": `${pageUrl}#page`,
        url: pageUrl,
        name: page.title,
        description: page.description,
        inLanguage: page.lang,
        mainEntity: { "@id": `${SITE_URL}/#stephane-nkengue` },
        isPartOf: {
          "@type": "WebSite",
          "@id": `${SITE_URL}/#website`,
          url: SITE_URL,
          name: person.name,
          inLanguage: page.lang,
        },
      },
    ],
  };
};

// Liens hreflang : indiquent à Google que les deux pages sont
// la même chose dans deux langues (et évitent le duplicate content).
const alternates = [
  { hreflang: "fr-CA", href: `${SITE_URL}/` },
  { hreflang: "en", href: `${SITE_URL}/en` },
  { hreflang: "x-default", href: `${SITE_URL}/` },
];

const seoConfig = {
  siteUrl: SITE_URL,
  person,
  pages,
  alternates,
  buildJsonLd,
  defaultLocale: "fr",
};

// CommonJS volontairement : ce fichier est importé par le bundle React (webpack
// fait l'interop vers un import par défaut) ET require() tel quel par
// scripts/prerender.js sous Node. Une seule source de vérité pour les deux.
module.exports = seoConfig;
